/** What the two management lists read out of the URL. */
export interface ListParams {
  /** 1-based, as the URL carries it. */
  page: number;
  limit: number;
  sort?: string | undefined;
  filters?: Record<string, string[]> | undefined;
}

/**
 * One canonical shape for the cache key.
 *
 * A loader reading the URL has `filters` undefined where the hook reading
 * `useTable` has `{}`, and `hashKey` drops undefined — so the two produced
 * different keys for the same list and the prefetch was thrown away. Both go
 * through here before the key is built.
 */
export function normalizeListParams({
  page,
  limit,
  sort,
  filters,
}: ListParams): Required<Omit<ListParams, 'sort'>> & { sort: string | null } {
  return { page, limit, sort: sort ?? null, filters: filters ?? {} };
}

/**
 * The query string both risk lists send.
 *
 * The service pages from 0 while the URL counts from 1, so the shift lives here
 * rather than anywhere a caller could forget it — and the loader and the hook
 * build the same string from the same input, which is what lets one prefetch
 * the other's cache entry.
 */
export function buildListQs({
  page,
  limit,
  sort,
  filters,
}: ListParams): string {
  const qs = new URLSearchParams();
  qs.set('page', String(Math.max(0, page - 1)));
  qs.set('count', String(limit));
  if (sort) qs.set('sort', sort);

  const filter: Record<string, unknown> = {};
  for (const [key, values] of Object.entries(filters ?? {})) {
    if (values.length > 0) filter[key] = values.join(',');
  }
  qs.set('filter', JSON.stringify(filter));

  return qs.toString();
}
